import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import cx from "classnames";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { CalendarContainer } from "@/components/ui/Calendar/StyledCalendar";


import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import CustomSelect from "@/components/ui/CustomSelect";

type PopperPlacement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "right"
  | "right-start"
  | "right-end"
  | "left"
  | "left-start"
  | "left-end";

export interface CalendarProps {
  selectedDate?: Date | string | null | undefined;
  minDate?: Date;
  maxDate?: Date;
  $minTime?: Date;
  $maxTime?: Date;
  onChange?: (date: Date | string) => void;
  onMonthChange?: (date: Date) => void;
  onYearChange?: (year: Date) => void;
  interval?: number;
  placeholder?: string;
  $placeholderColor?: string;
  label?: string;
  error?: string;
  value?: string;
  required?: boolean;
  $marginbottom?: string;
  border?: string;
  $isClearable?: boolean;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  customInput?: React.ReactElement | undefined;
  readOnly?: boolean;
  requestData?: string[];
  inline?: boolean;
  wrapperClassName?: string;
  filteredDates?: string[];
  className?: string;
  height?: string;
  width?: string;
  $minWidth?: string;
  $showIcon?: boolean;
  icon?: string | React.ReactElement;
  withPortal?: boolean;
  monthsShown?: number;
  $popperPlacement?: PopperPlacement;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  showMonthYearPicker?: boolean;
  showFullMonthYearPicker?: boolean;
  $disableKeyboardNavigation?: boolean;
  $backgroundColor?: string;
  $showCustomHeader?: boolean;
  $showYearPicker?: boolean;
  $isDisabled?: boolean;
  $maxYearOffset?: number;
  $defaultStartYear?: number;
  $dataId?: string;
  $dateFormat?: string;
}

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  $isClearable,
  onChange,
  onMonthChange,
  onYearChange,
  placeholder,
  $placeholderColor = "#a5a5a5",
  interval = 15,
  minDate,
  maxDate,
  $minTime,
  label,
  error,
  value,
  required,
  height = "2.75rem",
  width = "100%",
  $minWidth,
  border,
  $marginbottom,
  withPortal,
  monthsShown = 1,
  icon,
  $showIcon = true,
  $isDisabled,
  customInput,
  showTimeSelectOnly,
  filteredDates,
  showTimeSelect,
  wrapperClassName = "custom-calendar-wrapper",
  className,
  readOnly,
  $maxTime,
  inline,
  requestData,
  showMonthYearPicker,
  showFullMonthYearPicker,
  $popperPlacement,
  $disableKeyboardNavigation = true,
  $backgroundColor,
  $showCustomHeader = true,
  $showYearPicker,
  $maxYearOffset = 100,
  $defaultStartYear = 1900,
  $dataId = "calendar-input",
  $dateFormat = "MMMM d, yyyy",
  ...props
}: CalendarProps) => {
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);
  const [monthChange, setMonthChange] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null | undefined>(selectedDate ? new Date(selectedDate) : null);

useEffect(() => {
    const scheduledDates: Date[] = [];
    if (requestData) {
      if (Array.isArray(requestData)) {
        requestData.forEach((time) => {
          scheduledDates.push(new Date(time));
        });
      }
      setHighlightedDates(scheduledDates);
    }
  }, [requestData]);

  const handleChange = (value: Date | null) => {
    if (!value) {
      setDate(null);
      if (onChange) {
        onChange("");
      }
      return;
    } else {
      setDate(value);
      if (onChange) {
        onChange(value);
      }
    }
  };

  const handleMonthChange = (date: Date) => {
    if (onMonthChange) {
      onMonthChange(date);
    }
    setMonthChange(true);
    if (filteredDates) {
      const scheduledDates: Date[] = [];
      if (Array.isArray(filteredDates)) {
        filteredDates.forEach((time) => {
          scheduledDates.push(new Date(time));
        });
      }
      setHighlightedDates(scheduledDates);
    }
  };

  const handleYearChange = (year: Date) => {
    if (onYearChange) {
      onYearChange(year);
    }
    if (filteredDates) {
      const scheduledDates: Date[] = [];
      if (Array.isArray(filteredDates)) {
        filteredDates.forEach((time) => {
          scheduledDates.push(new Date(time));
        });
      }
      setHighlightedDates(scheduledDates);
    }
  };

    const defaultStartYear = $defaultStartYear;
  const maxYearOffset = $maxYearOffset;
  const years = Array.from(
    { length: new Date().getFullYear() + maxYearOffset - defaultStartYear + 1 },
    (_, index) => defaultStartYear + index
  );

    const yearsOptions = years.map((year) => ({ value: year.toString(), label: year.toString() }));

  const months = Array.from({ length: 12 }, (_, index) => dayjs().month(index).format("MMMM"));
  const monthsOptions = months.map((month) => ({ value: month, label: month }));

  const renderCustomHeader = ({
    date,
    changeMonth,
    changeYear,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: {
    date: Date;
    changeMonth: (month: number) => void;
    changeYear: (year: number) => void;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
  }) => {
    return (
    <div className="customHeader">
      <div className="navButtonsGroup">
        <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} aria-label="Previous month">
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="yearAndMonthDropdowns flex-1 w-full">
        <CustomSelect
          value={months[date.getMonth()]}
          onChange={(value) => changeMonth(Number(months.findIndex((month) => month === value)))}
          options={monthsOptions}
          placeholder="Select month"
          isSearchable={true}
          className="monthCustomSelect flex-1"
        />
        <CustomSelect
          value={date.getFullYear().toString()}
          onChange={(value) => changeYear(Number(value))}
          options={yearsOptions}
          placeholder="Select year"
          isSearchable={true}
          className="yearCustomSelect"
        />
      
      </div>
      <div className="navButtonsGroup">
        <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled} aria-label="Next month">
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  )};  


  return (
    <CalendarContainer
      onChange={onChange}
      $marginbottom={$marginbottom ?? "2rem"}
      border={error ? "#DE534E" : (border ?? "#d1d5db")}
      height={height}
      width={width}
      $minWidth={$minWidth ?? "max-content"}
      required={required}
      $placeholderColor={$placeholderColor}
      {...props}
      className={cx(className && className, "flexCol")}
      $backgroundColor={$backgroundColor}
      $isDisabled={$isDisabled}
    >
      {label ? <label>{label}</label> : null}
      <DatePicker
        renderCustomHeader={$showCustomHeader ? renderCustomHeader : undefined}
        selected={date}
        onChange={(data: Date | null) => handleChange(data)}
        data-id={$dataId}
        minDate={monthChange ? undefined : minDate}
        maxDate={maxDate}
        maxTime={$maxTime}
        highlightDates={highlightedDates}
        minTime={$minTime}
        customInput={customInput}
        wrapperClassName={wrapperClassName}
        showMonthYearPicker={showMonthYearPicker}
        showFullMonthYearPicker={showFullMonthYearPicker}
        showIcon={$showIcon}
        icon={icon}
        toggleCalendarOnIconClick
        timeIntervals={interval}
        onMonthChange={(date) => handleMonthChange(date)}
        onYearChange={(year) => handleYearChange(year)}
        clearButtonClassName='clearButton'
        readOnly={readOnly}
        showTimeSelectOnly={showTimeSelectOnly}
        showYearPicker={$showYearPicker}
        placeholderText={placeholder}
        showTimeSelect={showTimeSelect}
        inline={inline}
        isClearable={value ? $isClearable : false}
        dateFormat={showTimeSelectOnly ? "h:mm aa" : showTimeSelect ? "MMMM d, yyyy h:mm aa" : $dateFormat}
        className={className}
        disabledKeyboardNavigation={$disableKeyboardNavigation}
        calendarStartDay={1}
        monthsShown={monthsShown}
        withPortal={withPortal}
        formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
        popperPlacement={$popperPlacement ? $popperPlacement : undefined}
        onKeyDown={(e) => e.preventDefault()}
        {...props}
        disabled={$isDisabled}
        preventOpenOnFocus={true}
      />
      {error ? <span className={"error"}>{error}</span> : null}
    </CalendarContainer>
  );
};
Calendar.displayName = "Calendar";

export default Calendar;
